import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function AkupunkturPage() {
  return (
    <>
      <Helmet>
        <title>Akupunktur i Risskov | Behandling hos Kita</title>
        <meta
          name="description"
          content="Oplev nænsom og effektiv akupunktur i Risskov. Vi behandler smerter, muskelspændinger, migræne og ubalance i kroppen. Læs mere og bestil tid."
        />
        <meta property="og:title" content="Akupunktur i Risskov | Behandling hos Kita" />
        <meta
          property="og:description"
          content="Oplev nænsom og effektiv akupunktur i Risskov. Vi behandler smerter, muskelspændinger, migræne og ubalance i kroppen. Læs mere og bestil tid."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/akupunktur" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Akupunktur i Risskov nær Aarhus",
            "url": "https://kita-akupunktur.dk/akupunktur",
            "description":
              "Nænsom akupunkturbehandling tilpasset din sensitivitet i Risskov nær Aarhus.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent from /design-elements/ (cherry-2.png) */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>BLID NÅLEBEHANDLING • KITA AKUPUNKTUR</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">
            Akupunktur med blid og præcis tilgang
          </h1>
          <p className="text-base sm:text-lg text-[#7a5b4f] mt-3 max-w-2xl">
            Nænsom nålebehandling i Risskov nær Lystrup, Egå og Aarhus – tilpasset din sensitivitet.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-14">
        {/* Section 1: Intro Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left / Main text */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf]/60 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Nålebehandling tilpasset din sensitivitet
              </h2>
            </div>
            
            <p className="text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              Mærker du smerter, uro, søvnproblemer eller spændinger, som bliver ved med at fylde i hverdagen? Akupunktur kan bruges ved konkrete gener som{" "}
              <Link to="/stress" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                stress
              </Link>
              ,{" "}
              <Link to="/migraene" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                hovedpine og migræne
              </Link>
              , fordøjelsesproblemer,{" "}
              <Link to="/led-og-muskelsmerter" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                smerter i muskler og led
              </Link>
              , søvnproblemer og{" "}
              <Link to="/ondt-i-kaeben" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                kæbespændinger
              </Link>
              .
            </p>
            
            <p className="text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              Hos{" "}
              <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
                Kita Akupunktur
              </Link>{" "}
              i Risskov nær Lystrup, Egå og Aarhus får du behandling hos en akupunktør, der arbejder med tynde, sterile nåle,{" "}
              <Link to="/japansk-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                japansk akupunktur
              </Link>
              ,{" "}
              <Link to="/oere-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                øre akupunktur
              </Link>
              , og NADA-akupunktur.
            </p>
            
            <p className="text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              Jeg vælger nålepunkter og metode ud fra dine symptomer, din sensitivitet og om behandlingen skal stå alene eller kombineres med øre akupunktur, NADA-akupunktur eller kostvejledning.
            </p>

            <div className="pt-2 flex flex-wrap gap-3">
              <span className="inline-flex items-center px-3.5 py-1.5 rounded-full glass-pill text-xs text-[#593427] font-medium border border-[#ffe0cf]">
                <i className="fa-solid fa-check text-[#d74814] mr-2" aria-hidden="true" />
                0,12 mm ultra-tynde sterile engangsnåle
              </span>
              <span className="inline-flex items-center px-3.5 py-1.5 rounded-full glass-pill text-xs text-[#593427] font-medium border border-[#ffe0cf]">
                <i className="fa-solid fa-check text-[#d74814] mr-2" aria-hidden="true" />
                RAB-registreret behandler
              </span>
              <span className="inline-flex items-center px-3.5 py-1.5 rounded-full glass-pill text-xs text-[#593427] font-medium border border-[#ffe0cf]">
                <i className="fa-solid fa-check text-[#d74814] mr-2" aria-hidden="true" />
                Tilskud fra “danmark” & sundhedsforsikringer
              </span>
            </div>
          </div>

          {/* Right / Hvad er akupunktur card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-36 opacity-20 pointer-events-none">
                <CherryBranchImg index={3} className="w-full h-auto" />
              </div>

              <div className="flex items-center space-x-2.5 relative z-10">
                <SakuraFlower className="w-5 h-5 text-[#d74814]" />
                <h2 className="text-xl sm:text-2xl font-serif text-[#593427]">
                  Hvad er akupunktur?
                </h2>
              </div>
              <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base relative z-10">
                Akupunktur er en del af traditionel østlig medicin og har været brugt i tusindvis af år til at forebygge og behandle både fysiske og psykiske ubalancer. Ved at stimulere kroppens nervebaner med tynde, sterile nåle i specifikke punkter, kan vi støtte kroppens naturlige evne til at hele sig selv. Behandlingen virker regulerende på nervesystemet og har en dyb afslappende effekt.
              </p>
              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2.5 relative z-10">
                <JapaneseBadge label="NATURLIG HELING" meaning="Støtter kroppens ressourcer" />
                <JapaneseBadge label="INDRE BALANCE" meaning="Afbalancerer krop & sind" />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Hvad kan behandlingen hjælpe dig med? (Compact 3-col grid) */}
        <section className="space-y-6 pt-4 border-t border-[#ffe0cf]">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-[#ffe0cf]/60 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <SakuraFlower className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hvad kan behandlingen hjælpe dig med?
              </h2>
              <p className="text-[#7a5b4f] text-sm sm:text-base mt-0.5">
                Akupunktur kan benyttes til at behandle mange forskellige problemstillinger, herunder:
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { title: "Stress og udbrændthed", link: "/stress" },
              { title: "Hovedpine og migræne", link: "/migraene" },
              { title: "Fordøjelsesproblemer" },
              { title: "Smerter i muskler og led", link: "/led-og-muskelsmerter" },
              { title: "Allergi og astma" },
              { title: "Søvnproblemer", link: "/stress" },
              { title: "Rygestop og vægtregulering", link: "/rygestop-behandling" },
              { title: "Smerte i kæbe", link: "/ondt-i-kaeben" },
              { title: "Problem med tyggemusklerne og kæbeleddet", link: "/ondt-i-kaeben" },
            ].map((item, i) => (
              <li
                key={i}
                className="rounded-xl glass-pill hover:bg-white transition-all shadow-xs border border-[#ffe0cf]"
              >
                {item.link ? (
                  <Link
                    to={item.link}
                    className="flex items-center justify-between p-3.5 group"
                  >
                    <div className="flex items-center">
                      <SakuraFlower className="w-3.5 h-3.5 text-[#d74814] mr-2.5 shrink-0" />
                      <span className="text-[#593427] text-sm font-medium group-hover:text-[#d74814] transition-colors">{item.title}</span>
                    </div>
                    <i className="fa-solid fa-arrow-right text-[11px] text-[#ffe0cf] group-hover:text-[#d74814] group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
                  </Link>
                ) : (
                  <div className="flex items-center p-3.5">
                    <SakuraFlower className="w-3.5 h-3.5 text-[#d74814] mr-2.5 shrink-0" />
                    <span className="text-[#593427] text-sm font-medium">{item.title}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 3: Tryg og nænsom behandling (2 cards) */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-28 opacity-15 pointer-events-none">
              <CherryBranchImg index={1} className="w-full h-auto" />
            </div>
            <h3 className="font-serif font-semibold text-lg text-[#593427] mb-2 flex items-center">
              <SakuraFlower className="w-4 h-4 mr-2 text-[#d74814]" />
              <span>Skånsomme metoder</span>
            </h3>
            <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
              Der anvendes udelukkende engangsnåle af højeste kvalitet. Nålene er sterile, ekstremt tynde og fleksible for minimalt ubehag.
            </p>
          </div>

          <div className="glass-card p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-28 opacity-15 pointer-events-none">
              <CherryBranchImg index={5} flip={true} className="w-full h-auto" />
            </div>
            <h3 className="font-serif font-semibold text-lg text-[#593427] mb-2 flex items-center">
              <SakuraFlower className="w-4 h-4 mr-2 text-[#d74814]" />
              <span>Individuel vurdering</span>
            </h3>
            <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
              Hver konsultation indledes med en kort samtale om din tilstand og respons på tidligere behandlinger, så vi hele tiden finjusterer.
            </p>
          </div>
        </section>

        {/* Section: Stress & Udbrændthed (id="stress") */}
        <section
          id="stress"
          className="scroll-mt-28 bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • NERVESYSTEM & RO</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Stress & udbrændthed
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
              <Link
                to="/stress"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] text-sm font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
              >
                <span>Læs undersiden</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base">
              <p>
                Når kroppen over længere tid udsættes for pres, overarbejde eller følelsesmæssig belastning, låser det autonome nervesystem sig i en konstant alarmtilstand. Det medfører forhøjet puls, indre uro, søvnbesvær og en krop, der føles drænet for energi.
              </p>
              <p>
                Akupunktur er en yderst effektiv og nænsom behandlingsform til at berolige nervesystemet. Ved at stimulere specifikke meridianpunkter dæmpes kortisolniveauet og det beroligende parasympatiske nervesystem aktiveres, så du mærker dyb kropslig og mental ro indfinde sig.
              </p>
            </div>

            <div className="lg:col-span-5 bg-[#fffaf7] p-5 rounded-2xl border border-[#ffe0cf] space-y-3">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-check-circle text-[#d74814]" aria-hidden="true" />
                <span>Effekt ved stress og indre uro:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Dæmper stresshormoner og skaber dyb mental ro</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Forbedrer nattesøvnen og dæmper tankemylder</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Løsner spændinger for brystet og giver friere vejrtrækning</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Nænsom japansk tilgang tilpasset sensitivitet</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Spændinger (id="spaendinger") */}
        <section
          id="spaendinger"
          className="scroll-mt-28 glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • MUSKELAFSPÆNDING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Muskelspændinger
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
              <Link
                to="/spaendinger"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] text-sm font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
              >
                <span>Læs undersiden</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base">
              <p>
                Kroniske muskelspændinger i nakke, skuldre, ryg eller kæbe opstår hyppigt på grund af ensformigt skrivebordsarbejde, fysisk overbelastning eller længerevarende psykisk stress. Spændingerne begrænser blodcirkulationen og danner smertefulde myoser.
              </p>
              <p>
                Ved at placere akupunkturnåle i udvalgte triggerpunkter og meridianer sendes afspændende signaler direkte til nervesystemet. Muskelfibrene slapper af, blodcirkulationen stimuleres, og affaldsstoffer transporteres væk, hvilket genskaber bevægeligheden og letheden i kroppen.
              </p>
            </div>

            <div className="lg:col-span-5 bg-white/90 p-5 rounded-2xl border border-[#ffe0cf] space-y-3">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-check-circle text-[#d74814]" aria-hidden="true" />
                <span>Effekt ved muskelspændinger:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Opløser dybe myoser og muskelknuder</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Lindrer spændingshovedpine udløst fra nakke og skuldre</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Genskaber normal smidighed og bevægelsesfrihed</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Dyb ro og afstresning i hele kroppen</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Ondt i ryggen (id="ondt-i-ryggen") */}
        <section
          id="ondt-i-ryggen"
          className="scroll-mt-28 bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • RYG & LÆND</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Ondt i ryggen
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
              <Link
                to="/ondt-i-ryggen"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] text-sm font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
              >
                <span>Læs undersiden</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base">
              <p>
                Ryg- og lændesmerter er blandt de mest almindelige smerteproblemer i hverdagen. Det kan være akutte hold i ryggen (lumbago), overbelastede facetled, stivhed mellem skulderbladene eller udstrålende iskiassmerter ned i ballen og benet.
              </p>
              <p>
                Akupunktur er anerkendt som en yderst virksom behandlingsform ved rygsmerter. Nålestikningen dæmper den lokale inflammation, reducerer krampagtige sammentrækninger i rygmuskulaturen og fremmer kroppens egen produktion af smertelindrende endorfiner, så du kan bevæge dig frit igen.
              </p>
            </div>

            <div className="lg:col-span-5 bg-[#fffaf7] p-5 rounded-2xl border border-[#ffe0cf] space-y-3">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-check-circle text-[#d74814]" aria-hidden="true" />
                <span>Hjælp ved rygsmerter:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Lindring af akutte og kroniske lændesmerter</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Dæmper iskiassmerter og udstråling i benene</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Afhjælper hold i ryggen og stivhed langs rygsøjlen</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Naturlig smertelindring uden medicinske bivirkninger</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Led og muskelsmerter (id="led-og-muskelsmerter") */}
        <section
          id="led-og-muskelsmerter"
          className="scroll-mt-28 glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FOKUSOMRÅDE • LED- & MUSKELSMERTER I RISSKOV</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Akupunktur mod smerter i muskler og led
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/led-og-muskelsmerter"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-[#ffe0cf] text-[#593427] hover:border-[#d74814] text-xs sm:text-sm font-medium transition-colors shadow-2xs"
              >
                <span>Læs hele artiklen</span>
                <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814]" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#d74814] text-white text-xs sm:text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
              >
                <span>Bestil tid</span>
              </Link>
            </div>
          </div>

          <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
            <p className="text-lg font-medium text-[#593427]">
              Når smerter i muskler eller led fylder, kan selv små opgaver føles krævende. Hos Kita Akupunktur i Risskov nær Aarhus tilbyder jeg individuelt tilpasset akupunktur ved smerter i led og muskler samt spændinger. Vi tager udgangspunkt i dine gener, din hverdag og det, du gerne vil kunne igen.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
              <h3 className="font-serif font-semibold text-[#593427] text-lg">
                Hvor mærker du smerter og spændinger i hverdagen?
              </h3>
              <p>
                Til den første samtale spørger jeg ind til, hvor smerterne sidder, hvor længe de har været der, og hvad der gør dem bedre eller værre. Vi taler også om søvn, arbejde og bevægelse. Fortæl gerne, hvis du allerede har fået en diagnose, tager medicin eller er i et andet behandlingsforløb.
              </p>
              <p>
                Muskelsmerter kan føles som ømhed, spænding eller træthed. Giver dine led smerter ved bestemte bevægelser, er det vigtigt at få afklaret årsagen. Symptomerne fortæller ikke altid, hvad der ligger bag.
              </p>

              <div className="pt-2">
                <Link
                  to="/led-og-muskelsmerter"
                  className="inline-flex items-center space-x-2 text-sm font-semibold text-[#d74814] hover:text-[#b83b0e] underline decoration-[#ffe0cf]"
                >
                  <span>Gå til den dedikerede underside om led- og muskelsmerter</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white/90 p-5 rounded-2xl border border-[#ffe0cf] space-y-3 shadow-2xs">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-feather-pointed text-[#d74814]" aria-hidden="true" />
                <span>Nænsom japansk akupunktur:</span>
              </h3>
              <ul className="space-y-2 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Meget tynde sterile nåle (helt ned til 0,12 mm)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Mulighed for berørende kontaktnåle uden indstik</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Traditionel puls- og mavediagnose</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#d74814] font-bold">•</span>
                  <span>Rolig samtale og tilpasning efter din sensitivitet</span>
                </li>
              </ul>

              <div className="pt-3 border-t border-[#ffe0cf]/70 flex items-center justify-between text-xs text-[#593427]">
                <span>
                  <i className="fa-solid fa-location-dot text-[#d74814] mr-1" aria-hidden="true" />
                  Kantorparken 11, Risskov
                </span>
                <span className="text-[#d74814] font-medium">Tlf. 29 38 71 86</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Related Links */}
        <section className="pt-4 border-t border-[#ffe0cf]">
          <h3 className="text-xl font-serif text-[#593427] mb-4">
            Læs mere om specifikke behandlingsformer & fokusområder:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/japansk-akupunktur"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-feather-pointed text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Japansk akupunktur
                  </span>
                  <span className="text-xs text-[#7a5b4f]">0,12 mm nåle & mavediagnose</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/oere-akupunktur"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-ear-listen text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Øre akupunktur
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Mikrosystem & kugler</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/migraene"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-head-side-virus text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Migræne & hovedpine
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Nænsom smertelindring</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/ondt-i-kaeben"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-teeth text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Ondt i kæben
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Tyggemuskler & bidfunktion</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/rygestop-behandling"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-ban-smoking text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Rygestop behandling
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Dæmp nikotintrangen</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/vaegtregulering"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-scale-balanced text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Vægtregulering
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Nåle & kostvejledning</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/spaendinger"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-person-dots-from-line text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Spændinger i nakke & skulder
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Myoser & infiltrationer</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/ondt-i-ryggen"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-bone text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Ondt i ryggen & lænd
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Lændehold & iskias</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Hurtige genveje */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs sm:text-sm">
          <Link
            to="/priser"
            className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
          >
            Se priser & tilskud fra “danmark”
          </Link>
          <Link
            to="/om-mig"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Læs om Mayumi Kita Thiele
          </Link>
          <Link
            to="/faq"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Samlet FAQ
          </Link>
          <Link
            to="/kontakt"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Bestil tid i Risskov
          </Link>
        </div>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om akupunktur"
            subtitle="Få svar på de mest almindelige spørgsmål om akupunkturbehandling, nåle, forløb og tilskud hos Kita Akupunktur i Risskov."
            faqs={[
              {
                question: "Hvordan foregår en akupunkturbehandling hos Kita Akupunktur?",
                answer: (
                  <>
                    Ved din første konsultation starter vi med en grundig samtale om dine symptomer, din sygehistorie og din generelle trivsel. Derefter foretager jeg en nænsom puls- og kropsdiagnose (som i{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk akupunktur
                    </Link>
                    ) for at afdække eventuelle ubalancer i kroppens energibaner. Selve nålebehandlingen foregår i en rolig atmosfære på briksen, hvor sterile engangsnåle placeres på udvalgte akupunkturpunkter, mens du slapper af i cirka 20-30 minutter.
                  </>
                ),
              },
              {
                question: "Gør det ondt at få akupunkturnåle sat i?",
                answer:
                  "Nej, akupunkturnåle er ekstremt tynde – langt finere end kanyler, der bruges ved blodprøver eller vaccinationer hos lægen. De fleste klienter mærker blot et mikroskopisk prik efterfulgt af en let varme, tyngde eller prikken (De-Qi), når nålen aktiverer meridianpunktet. Jeg benytter en særlig skånsom og nænsom indføringsteknik, så hele oplevelsen føles behagelig og dybt afstressende.",
              },
              {
                question: "Hvor mange behandlinger skal der typisk til, før jeg mærker en effekt?",
                answer: (
                  <>
                    Reaktionen varierer afhængigt af, om der er tale om et akut opstået problem eller en længerevarende kronisk tilstand i kroppen. Mange mærker en mærkbar lindring, bedre søvn eller færre smerter allerede efter 1-3 behandlinger. Ved mere komplekse gener som f.eks.{" "}
                    <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      stress
                    </Link>{" "}
                    eller{" "}
                    <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      migræne
                    </Link>{" "}
                    anbefaler jeg typisk et forløb på 4-6 ugentlige sessioner.
                  </>
                ),
              },
              {
                question: "Kan jeg få tilskud fra Sygeforsikring 'danmark' eller min sundhedsforsikring?",
                answer: (
                  <>
                    Ja, da jeg er Registreret Alternativ Behandler (RAB), yder Sygeforsikring “danmark” tilskud til akupunktur, forudsat at du har en lægelig diagnose på dit problem. Rigtig mange private sundhedsforsikringer gennem arbejdspladsen eller pensionskasser dækker ligeledes udgifterne. Se takster og betingelser under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Er der bivirkninger eller særlige forholdsregler efter behandlingen?",
                answer: (
                  <>
                    Akupunktur er en naturlig behandlingsform uden medicinske bivirkninger. De mest almindelige reaktioner efter en session er en behagelig træthed, øget tørst og dyb mental afspænding, da nervesystemet og kredsløbet stimuleres. Drik gerne rigeligt med vand efterfølgende. Har du spørgsmål, er du altid velkommen til at{" "}
                    <Link to="/kontakt" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      kontakte mig
                    </Link>
                    .
                  </>
                ),
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}
